/**
 * G3 "highest paying" hub cluster — comparison pages targeting Keyword Planner
 * Low-competition head terms (opportunity score ~234.9). Each hub renders a
 * unique ranked OEWS wage table via api-top-occupations (or a curated filter).
 *
 * Existing surfaces that already answer head terms are listed with
 * `existingRoute` and do not need a duplicate page — they still count toward
 * the 12-hub goal for internal linking and reporting.
 */

export type HubApiType = 'high_paying' | 'no_degree' | 'entry_level' | 'ai_economy';

export interface HighestPayingHubConfig {
  /** URL slug under /highest-paying/:slug/ */
  slug: string;
  /** Primary keyword / H1 */
  title: string;
  /** Meta description seed */
  description: string;
  /** Short editorial intro (unique per hub) */
  intro: string;
  /** Methodology note shown under the table */
  methodology: string;
  /** api-top-occupations type filter */
  apiType: HubApiType;
  /** Optional SOC major-group prefix filter (e.g. '29' healthcare) applied client-side */
  socMajorPrefix?: string;
  /** Minimum mean salary filter (client-side, after API) */
  minMeanSalary?: number;
  /** Max rows to show */
  limit?: number;
  /**
   * If set, this hub already lives at another route — do not register a
   * duplicate /highest-paying/:slug page; link to this instead.
   */
  existingRoute?: string;
}

export const HIGHEST_PAYING_HUBS: HighestPayingHubConfig[] = [
  {
    slug: 'jobs-without-a-degree',
    title: 'Highest Paying Jobs Without a Degree',
    description:
      'Highest paying jobs without a degree, ranked from official BLS OEWS wage data.',
    intro: '',
    methodology: '',
    apiType: 'no_degree',
    existingRoute: '/no-degree-jobs/',
  },
  {
    slug: 'entry-level-jobs',
    title: 'Best Paying Entry-Level Jobs',
    description:
      'Best paying entry-level jobs ranked from official BLS OEWS wage data.',
    intro: '',
    methodology: '',
    apiType: 'entry_level',
    existingRoute: '/best-paying-entry-level/',
  },
  {
    slug: 'ai-jobs',
    title: 'Highest Paying AI Jobs',
    description:
      'Highest paying AI and AI-adjacent jobs from Wage Atlas curated OEWS occupations.',
    intro: '',
    methodology: '',
    apiType: 'ai_economy',
    existingRoute: '/ai-economy-jobs/',
  },
  {
    slug: 'jobs',
    title: 'Best Paying Jobs in the United States',
    description:
      'Best paying jobs in the United States ranked by mean annual wage from BLS OEWS.',
    intro:
      'These are the highest-paid occupations in the federal OEWS survey — employer-reported wages, not job-board estimates. Rankings use national mean annual wage; open any row for state-level percentiles, real (RPP-adjusted) pay, and outlook.',
    methodology:
      'Ranked by national annual mean wage from the latest BLS Occupational Employment and Wage Statistics release. Equity and bonus compensation are largely excluded from OEWS figures, so tech and finance totals can understate total pay.',
    apiType: 'high_paying',
    minMeanSalary: 75000,
    limit: 25,
  },
  {
    slug: 'careers',
    title: 'Highest Paying Careers',
    description:
      'Highest paying careers in the U.S. ranked from official BLS OEWS mean wages.',
    intro:
      'Career-level pay is driven by scarce skills, credential barriers, and industry margins. This ranking surfaces occupations where the national mean wage sits in the upper tier of the OEWS distribution — useful when comparing long-horizon career paths rather than single job offers.',
    methodology:
      'Occupations with national mean annual wage of $90,000 or more, ordered by mean wage. Source: BLS OEWS. Medians and percentiles are on each occupation’s salary page.',
    apiType: 'high_paying',
    minMeanSalary: 90000,
    limit: 25,
  },
  {
    slug: 'trade-jobs',
    title: 'Highest Paying Trade Jobs',
    description:
      'Highest paying trade and skilled craft jobs ranked from BLS OEWS wage data.',
    intro:
      'Trade occupations — electricians, welders, mechanics, construction trades — combine strong mid-career pay with apprenticeship pathways that often skip a four-year degree. Figures below are OEWS employer-reported wages for the United States.',
    methodology:
      'Filtered to Construction & Extraction (SOC 47), Installation/Maintenance/Repair (49), Production (51), and Transportation (53) major groups with mean wage ≥ $55,000, ranked by national mean wage.',
    apiType: 'high_paying',
    minMeanSalary: 55000,
    limit: 25,
    // Client filters to trade majors; api still returns high_paying pool
  },
  {
    slug: 'healthcare-jobs',
    title: 'Highest Paying Healthcare Jobs',
    description:
      'Highest paying healthcare jobs ranked from official BLS OEWS wage data.',
    intro:
      'Healthcare pay spans certified nursing assistants to physicians. This ranking focuses on Healthcare Practitioners and Technical occupations (SOC 29) where credential intensity and specialty drive the top of the wage distribution.',
    methodology:
      'SOC major group 29 (Healthcare Practitioners and Technical Occupations), ranked by national mean annual wage from BLS OEWS.',
    apiType: 'high_paying',
    socMajorPrefix: '29',
    limit: 25,
  },
  {
    slug: 'finance-jobs',
    title: 'Highest Paying Finance Jobs',
    description:
      'Highest paying finance and business jobs ranked from BLS OEWS wage data.',
    intro:
      'Finance and business operations roles pay differently by industry — the same analyst title earns more in capital markets than in nonprofits. These OEWS figures reflect employer-reported wages across all industries.',
    methodology:
      'SOC major groups 11 (Management) and 13 (Business and Financial Operations) with mean wage ≥ $80,000, ranked by national mean wage.',
    apiType: 'high_paying',
    minMeanSalary: 80000,
    limit: 25,
  },
  {
    slug: 'technology-jobs',
    title: 'Highest Paying Technology Jobs',
    description:
      'Highest paying technology and computer jobs ranked from BLS OEWS wage data.',
    intro:
      'Computer and mathematical occupations remain among the best-paid fields in OEWS, though equity compensation at product companies is mostly outside the survey. Use state pages and the RPP toggle for local purchasing power.',
    methodology:
      'SOC major group 15 (Computer and Mathematical Occupations), ranked by national mean annual wage from BLS OEWS.',
    apiType: 'high_paying',
    socMajorPrefix: '15',
    limit: 25,
  },
  {
    slug: 'management-jobs',
    title: 'Highest Paying Management Jobs',
    description:
      'Highest paying management jobs ranked from official BLS OEWS wage data.',
    intro:
      'Management wages track the scope of what is managed — budget, headcount, and industry margins — more than tenure alone. The spread within management titles is among the widest in the labor data.',
    methodology:
      'SOC major group 11 (Management Occupations), ranked by national mean annual wage from BLS OEWS.',
    apiType: 'high_paying',
    socMajorPrefix: '11',
    limit: 25,
  },
  {
    slug: 'engineering-jobs',
    title: 'Highest Paying Engineering Jobs',
    description:
      'Highest paying engineering jobs ranked from official BLS OEWS wage data.',
    intro:
      'Engineering wages are steady and licensure-sensitive. Petroleum, semiconductor, and aerospace specialties typically sit above civil and environmental averages; the PE stamp gates much of the top of the distribution.',
    methodology:
      'SOC major group 17 (Architecture and Engineering Occupations), ranked by national mean annual wage from BLS OEWS.',
    apiType: 'high_paying',
    socMajorPrefix: '17',
    limit: 25,
  },
  {
    slug: 'legal-jobs',
    title: 'Highest Paying Legal Jobs',
    description:
      'Highest paying legal jobs ranked from official BLS OEWS wage data.',
    intro:
      'Legal occupation wages are highly bimodal — large-firm and specialized counsel sit far above the median attorney. Prefer medians and percentiles on each salary page over the mean alone.',
    methodology:
      'SOC major group 23 (Legal Occupations), ranked by national mean annual wage from BLS OEWS.',
    apiType: 'high_paying',
    socMajorPrefix: '23',
    limit: 20,
  },
];

/** Hubs that need a dedicated /highest-paying/:slug page (not aliases). */
export const NEW_HIGHEST_PAYING_HUBS = HIGHEST_PAYING_HUBS.filter((h) => !h.existingRoute);

export function getHighestPayingHub(slug?: string): HighestPayingHubConfig | undefined {
  if (!slug) return undefined;
  return HIGHEST_PAYING_HUBS.find((h) => h.slug === slug);
}

/** Trade SOC major groups used by the trade-jobs hub filter. */
export const TRADE_SOC_MAJORS = new Set(['47', '49', '51', '53']);

/** Finance hub uses management + business majors. */
export const FINANCE_SOC_MAJORS = new Set(['11', '13']);

/**
 * Hubs relevant to a given SOC for salary→hub backlinks.
 * Always includes the general "best paying jobs" hub plus sector matches.
 */
export function hubsForOccupation(socCode?: string): HighestPayingHubConfig[] {
  const major = socCode?.slice(0, 2);
  const always = HIGHEST_PAYING_HUBS.filter((h) =>
    ['jobs', 'careers', 'jobs-without-a-degree', 'entry-level-jobs'].includes(h.slug)
  );

  const sector = HIGHEST_PAYING_HUBS.filter((h) => {
    if (!major) return false;
    if (h.slug === 'trade-jobs') return TRADE_SOC_MAJORS.has(major);
    if (h.slug === 'finance-jobs') return FINANCE_SOC_MAJORS.has(major);
    if (h.socMajorPrefix) return major === h.socMajorPrefix;
    if (h.slug === 'ai-jobs') return major === '15';
    return false;
  });

  const seen = new Set<string>();
  return [...sector, ...always].filter((h) => {
    if (seen.has(h.slug)) return false;
    seen.add(h.slug);
    return true;
  });
}
